/**
 * useTaskTemplates — React hook for task template CRUD via Template primitive.
 *
 * Replaces localStorage-based template storage with proper backend persistence:
 * - DynamoDB (primary) + Valkey (cache) + audit trails
 * - Builtin templates seeded under system tenant (0), visible to all
 * - User templates scoped to current tenant
 * - Draft → PendingApproval → Published lifecycle
 *
 * Uses TemplateRepository (repository pattern) for all data access.
 *
 * FERP Principles:
 * - Repository pattern: All access via TemplateRepository
 * - Backend IDs: Snowflake IDs generated server-side
 * - Data-driven: TaskFormState stored in Template.metadata.task_form_state
 * - Immutable audit: All mutations tracked (WHO/WHEN/WHAT)
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { notifications } from '@mantine/notifications';
import {
  createTemplateRepository,
  type TemplateRepository,
} from '../repositories/TemplateRepository';
import type { TaskFormState } from '../enhanced/taskFormState';
import { BUILTIN_SCENARIOS, type BuiltinScenario } from '../enhanced/taskFormState';
import { useAuth } from './useAuth';

// ============================================================================
// Types
// ============================================================================

/** Metadata for saving a new template */
export interface TemplateMeta {
  name: string;
  description: string;
  category: string;
  tags: string[];
}

/** Unified view of a task template (builtin or user-created) */
export interface TaskTemplateView {
  id: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  status: string;
  isBuiltin: boolean;
  formState: Partial<TaskFormState> | null;
  createdAt: string;
  publishedAt: string | null;
  usageCount: number;
  criticality: string;
  domain: string;
  color: string;
  features: string[];
}

/** Hook result */
export interface UseTaskTemplatesResult {
  /** All templates (builtins + user) */
  allTemplates: TaskTemplateView[];
  /** System-seeded builtin templates */
  builtinTemplates: TaskTemplateView[];
  /** User-created templates for current tenant */
  userTemplates: TaskTemplateView[];
  /** Loading state */
  loading: boolean;
  /** Error state */
  error: Error | undefined;
  /** Save a TaskFormState as a new Draft template */
  saveTemplate: (formState: TaskFormState, meta: TemplateMeta) => Promise<string | null>;
  /** Soft-delete a template */
  deleteTemplate: (id: string) => Promise<boolean>;
  /** Publish a Draft template (architect only) */
  publishTemplate: (id: string) => Promise<boolean>;
  /** Refetch from backend */
  refetch: () => void;
  /** Whether backend is available (false = using fallback) */
  isBackendAvailable: boolean;
}

// ============================================================================
// Serialization: TaskFormState ↔ Template.metadata
// ============================================================================

/**
 * Deserialize Template.metadata.task_form_state into a partial TaskFormState.
 */
function extractFormState(metadata: Record<string, unknown> | null): Partial<TaskFormState> | null {
  if (!metadata) return null;
  const tfs = metadata.task_form_state;
  if (!tfs || typeof tfs !== 'object') return null;
  return tfs as Partial<TaskFormState>;
}

/**
 * Convert a backend Template record into a TaskTemplateView.
 */
function templateToView(template: Record<string, unknown>): TaskTemplateView {
  const metadata = (template.metadata ?? {}) as Record<string, unknown>;
  return {
    id: String(template.id ?? ''),
    name: String(template.name ?? ''),
    description: String(template.description ?? ''),
    category: String(template.category ?? ''),
    tags: Array.isArray(template.tags) ? template.tags : [],
    status: String(template.status ?? 'Draft'),
    isBuiltin: metadata.builtin === true,
    formState: extractFormState(metadata),
    createdAt: String(
      (template.auditInfo as Record<string, unknown>)?.createdAt ?? template.createdAt ?? '',
    ),
    publishedAt: template.publishedAt ? String(template.publishedAt) : null,
    usageCount: Number(template.usageCount ?? 0),
    criticality: String(template.criticality ?? 'operational'),
    domain: String(
      (template.businessLayer as Record<string, unknown>)?.domain ?? metadata.domain ?? '',
    ),
    color: String(metadata.builtin_color ?? 'teal'),
    features: Array.isArray(metadata.features) ? metadata.features as string[] : [],
  };
}

/**
 * Convert a frontend BuiltinScenario to a TaskTemplateView (fallback when backend unavailable).
 */
function builtinToView(scenario: BuiltinScenario): TaskTemplateView {
  const d = scenario.initialData as Record<string, unknown>;
  const m = (d.metadata ?? {}) as Record<string, unknown>;

  // Build partial TaskFormState from the scenario (same logic as TaskTemplatePicker)
  const formState: Partial<TaskFormState> = {
    name: d.name as string,
    description: d.description as string,
    taskType: d.taskType as string,
    priority: d.priority as string,
    tags: d.tags as string[],
    domain: (d.domain ?? m.domain) as string,
    taskCategory: (d.taskCategory ?? m.taskCategory) as string,
    assignedTo: d.assignedTo as string,
    assignedTeam: d.assignedTeam as string,
    assignedRole: (d.assignedRole ?? m.assignedRole) as string,
    estimatedDuration: d.estimatedDuration as number,
    approvalRequired: d.approvalRequired as boolean,
    isCritical: m.isCritical as boolean,
    requiresImmediateAttention: m.requiresImmediateAttention as boolean,
    priorityScore: m.priorityScore as number,
    priorityReason: m.priorityReason as string,
    dualControl: m.dualControl as TaskFormState['dualControl'],
    requiresMfa: m.requiresMfa as boolean,
    securityLevel: m.securityLevel as string,
    approvalType: m.approvalType as string,
    approvers: m.approvers as string[],
    requiredRoles: m.requiredRoles as string[],
    requiredPermissions: m.requiredPermissions as string[],
    complianceFrameworks: m.complianceFrameworks as string[],
    regulatoryRequirements: m.regulatoryRequirements as string[],
    evidenceRequired: m.evidenceRequired as boolean,
    isConfidential: m.isConfidential as boolean,
    retentionPeriodDays: m.retentionPeriodDays as number,
    notificationChannels: m.notificationChannels as string[],
    escalationRules: m.escalationRules as TaskFormState['escalationRules'],
    escalationChain: m.escalationChain as string[],
    slaBufferMinutes: m.slaBufferMinutes as number,
    contextData: m.contextData as string,
    formSchema: m.formSchema as string,
    isParent: m.isParent as boolean,
    completionStrategy: m.completionStrategy as string,
    isSystemTask: m.isSystemTask as boolean,
    isAutomated: m.isAutomated as boolean,
    automationScript: m.automationScript as string,
    integrationId: m.integrationId as string,
    webhookUrl: m.webhookUrl as string,
    callbackUrl: m.callbackUrl as string,
    maxRetries: m.maxRetries as number,
    retryPolicy: m.retryPolicy as string,
  };

  // Remove undefined values
  const cleanState = Object.fromEntries(
    Object.entries(formState).filter(([, v]) => v !== undefined),
  ) as Partial<TaskFormState>;

  return {
    id: `builtin-${scenario.key}`,
    name: scenario.title,
    description: scenario.description,
    category: (d.taskCategory ?? d.domain ?? '') as string,
    tags: (d.tags ?? []) as string[],
    status: 'Published',
    isBuiltin: true,
    formState: cleanState,
    createdAt: '',
    publishedAt: null,
    usageCount: 0,
    criticality: scenario.showLifeCriticalFeatures ? 'life_critical' : 'operational',
    domain: (d.domain ?? '') as string,
    color: scenario.color,
    features: scenario.features,
  };
}

// ============================================================================
// Hook
// ============================================================================

const SYSTEM_TENANT = '0';

export function useTaskTemplates(): UseTaskTemplatesResult {
  const apolloClient = useApolloClient();
  const { currentUser, getCurrentTenantId } = useAuth();
  const tenantId = getCurrentTenantId();

  const [backendTemplates, setBackendTemplates] = useState<TaskTemplateView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const [isBackendAvailable, setIsBackendAvailable] = useState(false);

  const repo = useMemo<TemplateRepository>(
    () => createTemplateRepository(apolloClient as any),
    [apolloClient],
  );

  // Fetch templates from backend
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(undefined);

    try {
      // Fetch system tenant templates (builtins) + current tenant templates
      const [systemResult, tenantResult] = await Promise.all([
        repo.listByTenant(SYSTEM_TENANT, 100).catch(() => ({ items: [], totalCount: 0 })),
        tenantId !== SYSTEM_TENANT
          ? repo.listByTenant(tenantId, 100).catch(() => ({ items: [], totalCount: 0 }))
          : Promise.resolve({ items: [], totalCount: 0 }),
      ]);

      const allItems = [...systemResult.items, ...tenantResult.items];
      const views = allItems.map((t) => templateToView(t as unknown as Record<string, unknown>));
      setBackendTemplates(views);
      setIsBackendAvailable(true);
    } catch (err) {
      console.warn('⚠️ Backend templates unavailable, using fallback:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsBackendAvailable(false);
    } finally {
      setLoading(false);
    }
  }, [repo, tenantId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Fallback: use hardcoded BUILTIN_SCENARIOS when backend is unavailable
  const fallbackBuiltins = useMemo(
    () => BUILTIN_SCENARIOS.map(builtinToView),
    [],
  );

  // Split into builtin vs user
  const builtinTemplates = useMemo(
    () =>
      isBackendAvailable
        ? backendTemplates.filter((t) => t.isBuiltin)
        : fallbackBuiltins,
    [isBackendAvailable, backendTemplates, fallbackBuiltins],
  );

  const userTemplates = useMemo(
    () => backendTemplates.filter((t) => !t.isBuiltin),
    [backendTemplates],
  );

  const allTemplates = useMemo(
    () => [...builtinTemplates, ...userTemplates],
    [builtinTemplates, userTemplates],
  );

  // Save a new template as Draft
  const saveTemplate = useCallback(
    async (formState: TaskFormState, meta: TemplateMeta): Promise<string | null> => {
      try {
        const result = await repo.create({
          tenantId,
          name: meta.name,
          description: meta.description,
          version: '1.0.0',
          category: meta.category,
          tags: meta.tags,
          status: 'Draft' as any,
          criticality: 'operational',
          businessLayer: {
            domain: formState.domain || '',
            processName: meta.name.toLowerCase().replace(/\s+/g, '-'),
            icon: 'template',
            description: meta.description,
          },
          uiLayer: { layout: 'tabbed', steps: [] },
          primitiveLayer: { nodes: [], connections: [] },
          mappingsLayer: {
            uiToPrimitive: '{}',
            primitiveToUi: '{}',
            transformFunctions: '{}',
          },
          metadata: JSON.stringify({
            builtin: false,
            task_form_state: formState,
          }),
        } as any);

        notifications.show({
          title: 'Template Saved',
          message: `"${meta.name}" saved as draft. Submit for approval to publish.`,
          color: 'teal',
        });

        await fetchTemplates();
        return result.id;
      } catch (err) {
        console.error('Failed to save template:', err);
        notifications.show({
          title: 'Save Failed',
          message: err instanceof Error ? err.message : 'Failed to save template',
          color: 'red',
        });
        return null;
      }
    },
    [repo, tenantId, fetchTemplates],
  );

  // Soft-delete a template
  const deleteTemplate = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const success = await repo.delete(id as any, tenantId, currentUser.id);
        if (success) {
          notifications.show({
            title: 'Template Deleted',
            message: 'Template has been archived.',
            color: 'gray',
          });
          await fetchTemplates();
        }
        return success;
      } catch (err) {
        console.error('Failed to delete template:', err);
        notifications.show({
          title: 'Delete Failed',
          message: err instanceof Error ? err.message : 'Failed to delete template',
          color: 'red',
        });
        return false;
      }
    },
    [repo, tenantId, currentUser.id, fetchTemplates],
  );

  // Publish a Draft template
  const publishTemplate = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await repo.publish(id as any, tenantId, currentUser.id);
        notifications.show({
          title: 'Template Published',
          message: 'Template is now available for all users.',
          color: 'green',
        });
        await fetchTemplates();
        return true;
      } catch (err) {
        console.error('Failed to publish template:', err);
        notifications.show({
          title: 'Publish Failed',
          message: err instanceof Error ? err.message : 'Failed to publish template',
          color: 'red',
        });
        return false;
      }
    },
    [repo, tenantId, currentUser.id, fetchTemplates],
  );

  return {
    allTemplates,
    builtinTemplates,
    userTemplates,
    loading,
    error,
    saveTemplate,
    deleteTemplate,
    publishTemplate,
    refetch: fetchTemplates,
    isBackendAvailable,
  };
}
